import { PublicMarketplaceModule } from '@/@types/PublicMarketplace'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { userInitials, cn } from '@/lib/utils'
import { Check, ChevronDown } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type PreviewTab = 'content' | 'advantages' | 'details'

type DetailRow = {
  label: string
  value: string
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  productName: string
  productImageUrl?: string | null
  sellerName: string
  categoryName: string
  description: string
  advantages: string[]
  modules: PublicMarketplaceModule[]
  biography: string
  photoUrl: string | null
  details: DetailRow[]
}

const BIO_PREVIEW_LENGTH = 220

export function PublicMarketplacePreviewDialog({
  open,
  onOpenChange,
  productName,
  productImageUrl,
  sellerName,
  categoryName,
  description,
  advantages,
  modules,
  biography,
  photoUrl,
  details,
}: Props) {
  const { t } = useTranslation('common')
  const [tab, setTab] = useState<PreviewTab>('content')
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({})
  const [bioExpanded, setBioExpanded] = useState(false)
  const [productImageError, setProductImageError] = useState(false)

  useEffect(() => {
    setProductImageError(false)
  }, [productImageUrl])

  const previewModules = useMemo(
    () =>
      modules
        .filter((module) => module.title.trim())
        .map((module) => ({
          ...module,
          subtopics: (module.subtopics ?? [])
            .map((item) => item.trim())
            .filter(Boolean),
          description: module.description?.trim() || '',
        })),
    [modules],
  )

  const previewAdvantages = useMemo(
    () => advantages.map((item) => item.trim()).filter(Boolean),
    [advantages],
  )

  const previewDetails = useMemo(
    () => details.filter((row) => row.value.trim()),
    [details],
  )

  const tabs: { id: PreviewTab; label: string }[] = [
    { id: 'content', label: t('publicMarketplacePreviewContent') },
    { id: 'advantages', label: t('publicMarketplacePreviewAdvantages') },
    { id: 'details', label: t('publicMarketplacePreviewDetails') },
  ]

  const bioText = biography.trim()
  const bioNeedsTruncate = bioText.length > BIO_PREVIEW_LENGTH
  const visibleBio =
    bioExpanded || !bioNeedsTruncate
      ? bioText
      : `${bioText.slice(0, BIO_PREVIEW_LENGTH).trimEnd()}...`

  const displaySeller =
    sellerName.trim() || t('publicMarketplacePreviewProducerFallback')
  const displayCategory = categoryName.trim()
  const showProductImage = Boolean(productImageUrl) && !productImageError
  const productInitials = userInitials(
    productName.trim() || t('publicMarketplacePreviewUntitled'),
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setProductImageError(false)
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white text-zinc-900 border-zinc-200">
        <DialogHeader>
          <DialogTitle className="text-zinc-900">
            {t('publicMarketplacePreviewTitle')}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {t('publicMarketplacePreviewHelper')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {productName ? (
            <h3 className="text-xl font-semibold tracking-tight text-zinc-900">
              {productName}
            </h3>
          ) : null}

          <div
            className="relative flex min-h-[200px] flex-col items-center justify-center overflow-hidden rounded-2xl md:min-h-[260px]"
            style={{
              background:
                'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 48%, #C4B5FD 100%)',
            }}
          >
            {showProductImage ? (
              <img
                src={productImageUrl!}
                alt={productName || ''}
                className="absolute inset-0 h-full w-full object-cover"
                onError={() => setProductImageError(true)}
              />
            ) : (
              <>
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-sm md:h-24 md:w-24">
                  <span className="text-2xl font-bold tracking-wide text-violet-600 md:text-3xl">
                    {productInitials}
                  </span>
                </div>
                {productName ? (
                  <p className="mt-3 max-w-[70%] text-center text-sm font-semibold text-violet-700 md:text-base">
                    {productName.split('—')[0].trim().slice(0, 28)}
                  </p>
                ) : null}
              </>
            )}
          </div>

          <div className="space-y-3">
            <p className="text-sm text-zinc-500">
              {t('publicMarketplacePreviewBy', {
                name: displaySeller,
                category: displayCategory || t('publicMarketplacePreviewUntitled'),
              })}
            </p>
            {description ? (
              <p className="text-sm leading-relaxed text-zinc-700 whitespace-pre-line">
                {description}
              </p>
            ) : null}
          </div>

          <div className="flex gap-6 border-b border-zinc-200">
            {tabs.map((item) => (
              <Button
                key={item.id}
                type="button"
                variant="ghost"
                className={cn(
                  'rounded-none border-b-2 border-transparent px-0 py-2 text-sm font-medium text-zinc-400 hover:bg-transparent hover:text-zinc-900',
                  tab === item.id && 'border-zinc-900 text-zinc-900',
                )}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </Button>
            ))}
          </div>

          {tab === 'content' && (
            <div className="divide-y divide-zinc-200 border-y border-zinc-200">
              {previewModules.length === 0 ? (
                <p className="py-4 text-sm text-zinc-500">
                  {t('publicMarketplacePreviewEmptyModules')}
                </p>
              ) : (
                previewModules.map((module, index) => {
                  const moduleKey = module.id || `preview-module-${index}`
                  const isOpen = Boolean(openModules[moduleKey])
                  const hasBody =
                    Boolean(module.description) ||
                    (module.subtopics ?? []).length > 0

                  return (
                    <Collapsible
                      key={moduleKey}
                      open={isOpen}
                      onOpenChange={(next) =>
                        setOpenModules((prev) => ({
                          ...prev,
                          [moduleKey]: next,
                        }))
                      }
                    >
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 py-4 text-left"
                          disabled={!hasBody}
                        >
                          <span className="w-7 shrink-0 text-sm tabular-nums text-zinc-400">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <span className="flex-1 text-sm font-semibold text-zinc-900">
                            {module.title}
                          </span>
                          {hasBody ? (
                            <ChevronDown
                              className={cn(
                                'h-4 w-4 shrink-0 text-zinc-400 transition-transform',
                                isOpen && 'rotate-180',
                              )}
                            />
                          ) : null}
                        </button>
                      </CollapsibleTrigger>
                      {hasBody ? (
                        <CollapsibleContent>
                          <div className="space-y-3 pb-4 pl-10">
                            {module.description ? (
                              <p className="text-sm leading-relaxed text-zinc-500 whitespace-pre-line">
                                {module.description}
                              </p>
                            ) : null}
                            {(module.subtopics ?? []).length > 0 ? (
                              <ul className="space-y-2">
                                {(module.subtopics ?? []).map(
                                  (subtopic, subtopicIndex) => (
                                    <li
                                      key={`preview-subtopic-${index}-${subtopicIndex}`}
                                      className="flex items-start gap-2 text-sm text-zinc-800"
                                    >
                                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-zinc-400" />
                                      <span>{subtopic}</span>
                                    </li>
                                  ),
                                )}
                              </ul>
                            ) : null}
                          </div>
                        </CollapsibleContent>
                      ) : null}
                    </Collapsible>
                  )
                })
              )}
            </div>
          )}

          {tab === 'advantages' && (
            <div className="border-y border-zinc-200 py-4">
              {previewAdvantages.length === 0 ? (
                <p className="text-sm text-zinc-500">
                  {t('publicMarketplacePreviewEmptyAdvantages')}
                </p>
              ) : (
                <ul className="space-y-3">
                  {previewAdvantages.map((advantage, index) => (
                    <li
                      key={`preview-advantage-${index}`}
                      className="flex items-start gap-3 text-sm text-zinc-900"
                    >
                      <Check
                        className="mt-0.5 h-4 w-4 shrink-0 text-zinc-900"
                        strokeWidth={2.5}
                      />
                      <span>{advantage}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === 'details' && (
            <div className="divide-y divide-zinc-200 border-y border-zinc-200">
              {previewDetails.length === 0 ? (
                <p className="py-4 text-sm text-zinc-500">
                  {t('publicMarketplacePreviewEmptyDetails')}
                </p>
              ) : (
                previewDetails.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between gap-4 py-3.5 text-sm"
                  >
                    <span className="text-zinc-500">{row.label}</span>
                    <span className="text-right font-medium text-zinc-900">
                      {row.value}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}

          <div className="space-y-4 border-t border-zinc-200 pt-6">
            <h4 className="text-base font-semibold text-zinc-900">
              {t('publicMarketplacePreviewAboutCreator')}
            </h4>
            <div className="flex items-start gap-3">
              <Avatar className="h-12 w-12 shrink-0 bg-zinc-700">
                {photoUrl ? <AvatarImage src={photoUrl} alt="" /> : null}
                <AvatarFallback className="bg-zinc-700 text-sm font-medium text-zinc-100">
                  {userInitials(displaySeller)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-semibold text-zinc-900">
                  {displaySeller}
                </p>
                <p className="text-sm text-zinc-500">
                  {t('publicMarketplacePreviewProducerRole')}
                </p>
              </div>
            </div>
            {bioText ? (
              <div className="space-y-2">
                <p className="text-sm leading-relaxed text-zinc-700 whitespace-pre-line">
                  {visibleBio}
                </p>
                {bioNeedsTruncate ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm font-medium text-zinc-900"
                    onClick={() => setBioExpanded((prev) => !prev)}
                  >
                    {bioExpanded
                      ? t('publicMarketplacePreviewShowLess')
                      : t('publicMarketplacePreviewShowMore')}
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 transition-transform',
                        bioExpanded && 'rotate-180',
                      )}
                    />
                  </button>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-zinc-500">
                {t('publicMarketplacePreviewEmptyBiography')}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
